/* eslint-disable no-shadow */
import {
	createContext,
	FC,
	ReactNode,
	useContext,
	useEffect,
	useRef,
	useState,
	useMemo,
	useCallback,
} from 'react';
import { toast } from 'react-toastify';
import { useOpenTokContext } from '@/contexts/useOpenTok';
import { useSessionContext } from '@/contexts/useSession';
import { useDevicesContext } from '@/contexts/useDevices';
import { useGetDefaultBackgroundImageQuery } from '@/apollo/gql/generated/graphql';
import { useSubscribeToSignal, SignalTypes } from '@/contexts/useSignal';
import uniq from 'lodash/uniq';
import useTranslation from 'next-translate/useTranslation';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
	appStatusAtom,
	isSessionConnectedAtom,
} from '@/recoil/atom/globalState';
import { customBugsnagLogger } from '@/utils/bugsnag';
import { DeviceUsedByOtherProcess } from '@/components/deviceModals/deviceUsedByOtherProcess';
import useUpdateUser from '@/hooks/useUpdateUser';
import { DeviceError } from '@/components/deviceModals/deviceError';
import { events, severity } from '@/config/log_events';
import { PUBLISHER_MAX_RETRIES } from '@/constants/connection';
import { useTempDevicesContext } from './useTempDevices';
import { useRecordingContext } from './useRecorder';
import { useViewerContext } from './useViewer';
import { useLoggingContext } from './useLogging';

type Props = {
	classSlug: string;
	children: ReactNode;
};

type ContextType = {
	publisher?: OT.Publisher;
	tempPublisher?: OT.Publisher;
	videoElement?: HTMLVideoElement;
	tempVideoElement?: HTMLVideoElement;
	isLoading: boolean;
	publisherStream?: OT.Stream;
	tempStream?: OT.Stream;
	deviceStatus: {
		video: boolean;
		audio: boolean;
	};
	tempDeviceStatus: {
		video: boolean;
		audio: boolean;
	};
	setBackground: (url: string) => void;
	blurBackground: () => void;
	clearBackground: () => void;
	applyFilters: () => void;
	toggleDevice: (deviceType: 'video' | 'audio', temp?: boolean) => void;
	createTempPublisher: () => void;
	destroyTempPublisher: () => void;
	applyMic: () => void;
	lastInteracted: string[];
	loadingFilter: boolean;
	canApplyFilters: boolean;
	hasFilterApplied: boolean;
	hasClearedBackground: boolean;
	isReadyToPublish: boolean;
	retry: (reset?: boolean) => void;
};
const RETRIES_TIMEOUT = 1500;

const Context = createContext<ContextType>(undefined as unknown as ContextType);

const PublisherProvider: FC<Props> = ({ children, classSlug }: Props) => {
	const [hasClearedBackground, setHasClearedBackground] =
		useState<boolean>(false);
	const Opentok = useOpenTokContext();
	const { t } = useTranslation('default');
	const { getSession } = useSessionContext();
	const { updatePermissions } = useUpdateUser();
	const isConnected = useRecoilValue(isSessionConnectedAtom);
	const { logToLambda } = useLoggingContext();
	const {
		camera,
		mic,
		getStreams,
		permissionStatus,
		getMediaPermissionStatus,
		setDevice,
	} = useDevicesContext();

	const { viewer } = useViewerContext();
	const { camera: tempCamera, mic: tempMic } = useTempDevicesContext();
	const [publisher, setPublisher] = useState<OT.Publisher>();
	const [publisherStream, setPublisherStream] = useState<OT.Stream>();
	const [videoElement, setVideoElement] = useState<HTMLVideoElement>();
	const [isLoading, setIsLoading] = useState(true);
	const [showDeviceInUse, setShowDeviceInUse] = useState(false);
	const [deviceError, setDeviceError] = useState({
		show: false,
		name: '',
		type: '',
	});
	const [lastInteracted, setLastInteracted] = useState<string[]>([]);
	const [tempPublisher, setTempPublisher] = useState<OT.Publisher>();
	const [loadingFilter, setLoadingFilter] = useState<boolean>(false);
	const [canApplyFilters, setCanApplyFilters] = useState<boolean>(false);
	const [hasFilterApplied, setHasFilterApplied] = useState<boolean>(false);
	const [tempStream, setTempStream] = useState<OT.Stream>();
	const [tempVideoElement, setTempVideoElement] = useState<HTMLVideoElement>();
	const [retries, setRetries] = useState(0);
	const [isReadyToPublish, setIsReadyToPublish] = useState(false);
	const setAppStatus = useSetRecoilState(appStatusAtom);

	const [deviceStatus, setDeviceStatus] = useState<{
		video: boolean;
		audio: boolean;
	}>({
		video: false,
		audio: false,
	});

	const [tempDeviceStatus, setTempDeviceStatus] = useState<{
		video: boolean;
		audio: boolean;
	}>({
		video: true,
		audio: true,
	});

	const publisherRef = useRef<OT.Publisher>();
	const tempPublisherRef = useRef<OT.Publisher>();
	const tempVideoElementRef = useRef<HTMLVideoElement | undefined>();
	const stopExecutionRef = useRef<boolean>(false);
	const retryInFlightRef = useRef<boolean>(false);
	const trackWatchersCleanupRef = useRef<(() => void) | undefined>();

	useEffect(() => {
		tempVideoElementRef.current = tempVideoElement;
	}, [tempVideoElement]);

	const timeoutIdRef = useRef<NodeJS.Timeout>();
	const { data, loading } = useGetDefaultBackgroundImageQuery({
		variables: { filter: { classSlug } },
	});

	const subscribeToInteractions = useSubscribeToSignal<any>(
		SignalTypes.LAST_INTERACTIVE
	);

	const { updateRecording } = useRecordingContext();

	const retry = useCallback(
		(reset = false) => {
			if (retryInFlightRef.current) {
				customBugsnagLogger.debug(
					'Retry already in flight, skipping duplicate request'
				);
				return;
			}
			customBugsnagLogger.debug(
				`Retrying publisher creation (attempt ${retries + 1}/${PUBLISHER_MAX_RETRIES})`
			);
			if (retries === PUBLISHER_MAX_RETRIES) {
				toast(
					`Unable to create publisher. Please make sure you granted camera and microphone permissions and refresh the page.`,
					{
						type: 'error',
						hideProgressBar: true,
						autoClose: false,
					}
				);
				return;
			}
			retryInFlightRef.current = true;
			clearTimeout(timeoutIdRef.current);

			timeoutIdRef.current = setTimeout(() => {
				customBugsnagLogger.debug('Retry timeout fired, recreating publisher');
				publisherRef.current = undefined;
				stopExecutionRef.current = false;
				retryInFlightRef.current = false;
				setRetries((r) => (reset ? 0 : r + 1));
			}, RETRIES_TIMEOUT * (retries + 1));
		},
		[retries]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const updateLastInteracted = async (userId: string) => {
		setLastInteracted((interactions) => uniq([userId, ...interactions]));
	};

	const destroyTempPublisher = useCallback(async () => {
		const pub = tempPublisherRef.current;
		if (!pub) return;
		const videoEl = tempVideoElementRef.current;
		customBugsnagLogger.debug('destroying temp publisher');
		console.log('destroying temp publisher');
		tempPublisherRef.current = undefined;
		tempVideoElementRef.current = undefined;
		pub.destroy();
		(videoEl?.srcObject as MediaStream)?.getTracks().forEach((track) => track.stop());
		setTempPublisher(undefined);
		setTempStream(undefined);
		setTempVideoElement(undefined);
	}, []);

	useEffect(() => {
		if (classSlug?.startsWith('T0') || classSlug?.startsWith('t0')) {
			setCanApplyFilters(false);
			return;
		}
		if (OT.hasMediaProcessorSupport()) {
			setCanApplyFilters(true);
		}
	}, [classSlug]);

	useEffect(() => {
		if (!isConnected) return;
		subscribeToInteractions('*', (interaction) => {
			updateLastInteracted(interaction.userId);
		});
	}, [subscribeToInteractions, isConnected]);

	useEffect(() => {
		if (
			!isConnected ||
			publisherRef?.current ||
			loading ||
			// stopExecutionRef.current ||
			permissionStatus === 'denied'
		) {
			return;
		}
		// stopExecutionRef.current = true;
		console.log('starting publisher =======');
		if (!permissionStatus) {
			console.log('no permission getting streams');
			// stopExecutionRef.current = false;
			getStreams(true);
			return;
		}
		customBugsnagLogger.debug('Creating publisher');
		customBugsnagLogger.debug(`Permission Status ${permissionStatus}`);
		// stopExecutionRef.current = true;

		const session = getSession();
		const getVideoFilter = () => {
			if (!canApplyFilters) return undefined;
			const storedFilter = localStorage.getItem('cameraFilter');
			switch (storedFilter) {
				case 'none':
					return undefined;
				case undefined:
				case null:
					return {
						type: 'backgroundReplacement',
						backgroundImgUrl: `/aws-bucket/${
							data?.getDefaultBackgroundImage?.key as string
						}`,
					};
				default:
					return JSON.parse(storedFilter as string);
			}
		};

		setIsLoading(true);

		const savedFilter = getVideoFilter();

		if (savedFilter) {
			customBugsnagLogger.debug('Had filter applied');
			setHasFilterApplied(true);
		} else {
			customBugsnagLogger.debug('Did not have filter applied');
			setHasFilterApplied(false);
		}

		const publisherOptions: OT.PublisherProperties = {
			publishAudio: deviceStatus.audio,
			publishVideo: true,
			audioSource: mic?.deviceId === '' ? undefined : mic?.deviceId,
			videoSource: camera?.deviceId === '' ? undefined : camera?.deviceId,
			insertDefaultUI: false,
			width: '100%',
			height: '100%',
			videoFilter: savedFilter,
			audioFallback: {
				publisher: true,
				subscriber: true,
			},
			style: {
				videoDisabledDisplayMode: 'off',
			},
		};

		customBugsnagLogger.debug('Starting publisher creation');

		Opentok.on('exception', (error) => {
			if ((error as any)?.error?.name === 'OT_HARDWARE_UNAVAILABLE') {
				console.log('device in use');
				setShowDeviceInUse(true);
			}
			if (error?.message === 'An unknown error occurred') {
				customBugsnagLogger.debug(
					`EXCEPTION. An unknown error occurred, currentState: ${
						(error?.target as any)?.currentState || 'unknown'
					}`
				);
			} else if (
				(error?.target as any)?.currentState &&
				(error?.target as any)?.currentState !== 'connected'
			) {
				setAppStatus((prevStatus) => ({
					...prevStatus,
					publisherStatus: 2,
				}));
			}
			logToLambda(events.OT_ERROR, {
				user: viewer,
				severity: severity.ERROR,
				metadata: {
					message: error?.message,
					type: 'exception',
					name: (error as any)?.error?.name,
				},
				classSlug,
			});
			customBugsnagLogger.error(new Error(error?.message));
		});

		const publisher = Opentok.initPublisher(
			undefined,
			publisherOptions,
			(error) => {
				if (error) {
					customBugsnagLogger.debug('Publisher Init error');
					stopExecutionRef.current = false;
					setIsLoading(false);
					publisherRef.current = undefined;
					setAppStatus((prevStatus) => ({
						...prevStatus,
						publisherStatus: 2,
					}));
					logToLambda(events.OT_PUBLISHER_FAILED, {
						user: viewer,
						severity: severity.ERROR,
						metadata: { error },
						classSlug,
					});

					if (error.name === 'OT_USER_MEDIA_ACCESS_DENIED') {
						toast(
							`Unable to create publisher. Please make sure you granted camera and microphone permissions and refresh the page.`,
							{
								type: 'error',
								hideProgressBar: true,
							}
						);
						updatePermissions('denied');
						getMediaPermissionStatus('camera');
						customBugsnagLogger.debug(
							'User denied access to camera and microphone'
						);
						return;
					}
					if (error.name === 'OT_CONSTRAINTS_NOT_SATISFIED') {
						customBugsnagLogger.debug('OT_CONSTRAINTS_NOT_SATISFIED');
						setShowDeviceInUse(true);
						getStreams(true);
						return;
					}
					customBugsnagLogger.error(error);

					if (error.name === 'OT_HARDWARE_UNAVAILABLE') {
						setShowDeviceInUse(true);
						return;
					}
					toast(`Unable to create publisher. Reason:${error.message}`, {
						type: 'error',
						hideProgressBar: true,
					});

					return;
				}

				const publisherHasImage = publisher.getImgData();
				if (publisherOptions.videoSource && !publisherHasImage) {
					customBugsnagLogger.debug('Publisher did not have image');
					setIsLoading(false);
					publisher.cycleVideo();
					stopExecutionRef.current = false;
					return;
				}
				customBugsnagLogger.debug('Attempting publish to session');

				session?.publish(publisher, (error) => {
					if (error) {
						setIsLoading(false);
						console.log(error);
						customBugsnagLogger.debug('session publish error');
						customBugsnagLogger.error(error);
						toast(`Unable to publish to session. Reason:${error.message}`, {
							type: 'error',
							hideProgressBar: true,
						});
						stopExecutionRef.current = false;
						setAppStatus((prevStatus) => ({
							...prevStatus,
							publisherStatus: 2,
						}));
						retry();
						return;
					}
					logToLambda(events.OT_PUBLISHER_CREATED, {
						user: viewer,
						severity: severity.INFO,
						classSlug,
					});
					customBugsnagLogger.debug('Publisher Created!!!!!');
					publisherRef.current = publisher;
					publisher.publishVideo(deviceStatus.video);
					setPublisher(publisher);
					setIsLoading(false);
					setAppStatus((prevStatus) => ({
						...prevStatus,
						publisherStatus: 1,
					}));
					retryInFlightRef.current = false;

					// Attach MediaStreamTrack watchers as a non-OT liveness signal.
					// If the camera/mic track ends without OT firing streamDestroyed
					// (silent failure case from VC-1223), the FSM still detects it.
					trackWatchersCleanupRef.current?.();
					const videoTrack = publisher.getVideoSource()?.track as
						| MediaStreamTrack
						| undefined;
					const audioTrack = publisher.getAudioSource() as
						| MediaStreamTrack
						| undefined;

					const handleTrackEnded = (kind: 'video' | 'audio') => () => {
						customBugsnagLogger.debug(
							`Publisher ${kind} track ended (independent of OT)`
						);
						setAppStatus((prev) => ({
							...prev,
							publisherStatus: 2,
						}));
						logToLambda(events.PUBLISHER_TRACK_ENDED, {
							user: viewer,
							classSlug,
							metadata: { kind },
						});
					};

					const videoHandler = handleTrackEnded('video');
					const audioHandler = handleTrackEnded('audio');

					videoTrack?.addEventListener('ended', videoHandler);
					audioTrack?.addEventListener('ended', audioHandler);

					// Defensive: track may have ended between publish success and
					// listener attachment (race window is tiny but real).
					if (videoTrack?.readyState === 'ended') videoHandler();
					if (audioTrack?.readyState === 'ended') audioHandler();

					trackWatchersCleanupRef.current = () => {
						videoTrack?.removeEventListener('ended', videoHandler);
						audioTrack?.removeEventListener('ended', audioHandler);
					};
				});
			}
		);

		if (!publisher) return;

		const destroyed = () => {
			// publisherRef.current = undefined;
			stopExecutionRef.current = false;
			setPublisherStream(undefined);
			setPublisher(undefined);
			setVideoElement(undefined);
			console.log('publisher destroyed');
			customBugsnagLogger.debug('Publisher Destroyed');
			logToLambda(events.OT_PUBLISHER_DESTROYED, {
				user: viewer,
				severity: severity.INFO,
				classSlug,
			});
		};

		const videoElementCreated = ({
			element,
		}: OT.Event<'videoElementCreated', OT.Publisher> & {
			element: HTMLVideoElement | HTMLObjectElement;
		}) => {
			customBugsnagLogger.debug('Publisher Video element created');
			setVideoElement(element as HTMLVideoElement);
		};

		const streamCreated = ({
			stream,
		}: OT.Event<'streamCreated', OT.Publisher> & { stream: OT.Stream }) => {
			console.log('publisher stream created');
			customBugsnagLogger.debug('Publisher stream created');
			console.log(
				`User ${viewer?.id} publisher created stream from frontend container with IP ${process.env.CONTAINER_IP}`
			);
			setIsReadyToPublish(true);
			setPublisherStream(stream);
			setAppStatus((prevStatus) => ({
				...prevStatus,
				publisherStatus: 1,
			}));
			retryInFlightRef.current = false;
		};

		const mediaStopped = (event: OT.Event<'mediaStopped', OT.Publisher> & { track: MediaStreamTrack | undefined }) => {
			console.log('media stopped', event);
			event.preventDefault();

			const trackKind = event?.track?.kind || '';
			const trackLabel = event?.track?.label || "Couldn't get device name";

			if (
				(trackKind === 'video' && camera?.label !== trackLabel) ||
				(trackKind === 'audio' && mic?.label !== trackLabel)
			)
				return;

			console.log(
				`Publisher media stopped, type: ${trackKind}, name: ${trackLabel} showing modal`
			);
			customBugsnagLogger.debug(
				`Publisher media stopped, type: ${trackKind}, name: ${trackLabel} showing modal`
			);

			setDeviceError({
				show: true,
				name: trackLabel,
				type: trackKind,
			});

			setAppStatus((prevStatus) => ({
				...prevStatus,
				publisherStatus: 2,
			}));
		};

		const streamDestroyed = ({
			reason,
		}: OT.Event<'streamDestroyed', OT.Publisher> & {
			stream: OT.Stream;
			reason: string;
		}) => {
			customBugsnagLogger.debug(`Publisher stream destroyed, ${reason}`);
			if (reason === 'networkDisconnected' || reason === 'networkTimedout') {
				customBugsnagLogger.debug('Retrying Publisher creation');
				retry();
			}
			console.log('publisher stream destroyed', reason);
			setPublisherStream(undefined);
			setAppStatus((prevStatus) => ({
				...prevStatus,
				publisherStatus: 2,
			}));
		};

		const accessDialogOpened = () => {
			customBugsnagLogger.debug('Permissions dialog open');
		};

		const accessDialogClosed = () => {
			customBugsnagLogger.debug('Permissions dialog closed');
		};

		const accessDenied = () => {
			customBugsnagLogger.debug('Permissions for A/V DENIED!');
		};

		const videoDisableWarning = () => {
			toast(t('status.video_disable_warning'), {
				hideProgressBar: true,
				type: 'warning'
			});
			customBugsnagLogger.debug('Quality degradation detected!');
		};

		const videoDisableWarningLifted = () => {
			customBugsnagLogger.debug('Quality degradation lifted!');
		};

		const publisherHandlers = {
			destroyed,
			videoElementCreated,
			streamCreated,
			streamDestroyed,
			accessDialogOpened,
			accessDialogClosed,
			accessDenied,
			mediaStopped,
			videoDisableWarning,
			videoDisableWarningLifted,
		};

		publisher.on(publisherHandlers);

		return () => {
			console.log('publisher cleanup ==========');
			stopExecutionRef.current = false;
			if (publisher) {
				publisher.off(publisherHandlers);
				publisher.destroy();
			}
			setPublisher(undefined);
			setVideoElement(undefined);
			setIsLoading(false);
			publisherRef.current = undefined;
			retryInFlightRef.current = false;
			trackWatchersCleanupRef.current?.();
			trackWatchersCleanupRef.current = undefined;
			customBugsnagLogger.debug('Publisher clean up');
			Opentok.off('exception');
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isConnected,
		mic?.deviceId,
		camera?.deviceId,
		loading,
		data?.getDefaultBackgroundImage?.key,
		canApplyFilters,
		retries,
		permissionStatus,
		getSession,
		// Opentok,
		getStreams,
		setAppStatus,
		updatePermissions,
		getMediaPermissionStatus,
		retry,
		viewer?.id,
		// deviceStatus.audio,
		// deviceStatus.video,
	]);

	const createTempPublisher = useCallback(async () => {
		let filters;
		if (tempPublisherRef.current) return;
		if (publisher && canApplyFilters) {
			filters = publisher.getVideoFilter();
		}
		const tempPublisherOptions: OT.PublisherProperties = {
			publishAudio: tempDeviceStatus.audio,
			publishVideo: tempDeviceStatus.video,
			audioSource: tempMic?.deviceId === '' ? undefined : tempMic?.deviceId,
			videoSource:
				tempCamera?.deviceId === '' ? undefined : tempCamera?.deviceId,
			insertDefaultUI: false,
			width: '100%',
			height: '100%',
			videoFilter: filters || undefined,
			audioFallback: {
				publisher: true,
				subscriber: true,
			},
			style: {
				videoDisabledDisplayMode: 'off',
			},
		};

		const newTempPublisher = Opentok.initPublisher(
			undefined,
			tempPublisherOptions,
			(error) => {
				if (error) {
					customBugsnagLogger.error(error);
				}
				customBugsnagLogger.debug('Temporary Publisher Created');
				setTempPublisher(newTempPublisher);
				tempPublisherRef.current = newTempPublisher;
			}
		);
		if (!newTempPublisher) return;

		const videoElementCreated = ({
			element,
		}: OT.Event<'videoElementCreated', OT.Publisher> & {
			element: HTMLVideoElement | HTMLObjectElement;
		}) => {
			customBugsnagLogger.debug('Temporary Publisher Video element created');
			setTempVideoElement(element as HTMLVideoElement);
		};

		const streamCreated = ({
			stream,
		}: OT.Event<'streamCreated', OT.Publisher> & { stream: OT.Stream }) => {
			customBugsnagLogger.debug('Temporary Publisher stream created');
			setTempStream(stream);
		};

		const streamDestroyed = ({
			stream,
			reason,
		}: OT.Event<'streamDestroyed', OT.Publisher> & {
			stream: OT.Stream;
			reason: string;
		}) => {
			customBugsnagLogger.debug(
				`Temporary Publisher stream destroyed, ${reason}`
			);
			setTempStream(undefined);
		};

		const publisherHandlers = {
			videoElementCreated,
			streamCreated,
			streamDestroyed,
		};

		newTempPublisher.on(publisherHandlers);
		return () => {
			if (tempPublisherRef.current) {
				customBugsnagLogger.debug('Temporary Publisher clean up');
				destroyTempPublisher();
			}
		};
	}, [
		publisher,
		canApplyFilters,
		tempDeviceStatus.audio,
		tempDeviceStatus.video,
		tempMic?.deviceId,
		tempCamera?.deviceId,
		Opentok,
		destroyTempPublisher,
	]);

	const toggleDevice = useCallback(
		(deviceType: 'video' | 'audio', temp?: boolean) => {
			try {
				if (temp) {
					const newStatus = !tempDeviceStatus[deviceType];
					if (deviceType === 'video') {
						tempPublisher?.publishVideo(newStatus);
						setTempDeviceStatus({
							audio: tempDeviceStatus.audio,
							video: newStatus,
						});
					}
					if (deviceType === 'audio') {
						tempPublisher?.publishAudio(newStatus);
						setTempDeviceStatus({
							audio: newStatus,
							video: tempDeviceStatus.video,
						});
					}
				} else {
					const newStatus = !deviceStatus[deviceType];

					if (deviceType === 'video') {
						publisher?.publishVideo(newStatus);
						setDeviceStatus({ audio: deviceStatus.audio, video: newStatus });
					}
					if (deviceType === 'audio') {
						publisherRef?.current?.publishAudio(newStatus);
						setDeviceStatus({ audio: newStatus, video: deviceStatus.video });
					}
				}
			} catch (error) {
				console.log({ error });
			}
		},
		[deviceStatus, publisher, tempDeviceStatus, tempPublisher]
	);

	const blurBackground = useCallback(async () => {
		if (!tempPublisher) return;
		setLoadingFilter(true);
		await tempPublisher.clearVideoFilter();
		await tempPublisher.applyVideoFilter({
			type: 'backgroundBlur',
			blurStrength: 'high',
		});
		setLoadingFilter(false);
		customBugsnagLogger.debug('Blurring background');
	}, [tempPublisher]);

	const setBackground = useCallback(
		async (url) => {
			if (!tempPublisher) return;
			setLoadingFilter(true);

			await tempPublisher.clearVideoFilter();
			await tempPublisher.applyVideoFilter({
				type: 'backgroundReplacement',
				backgroundImgUrl: url,
			});
			setLoadingFilter(false);
			customBugsnagLogger.debug('Setting background');
		},
		[tempPublisher]
	);

	const clearBackground = useCallback(() => {
		if (!tempPublisher) return;
		tempPublisher.clearVideoFilter();
		setHasClearedBackground(true);
		customBugsnagLogger.debug('Clearing background');
	}, [tempPublisher]);

	const applyFilters = useCallback(async () => {
		if (!publisher) {
			retry();
		}
		customBugsnagLogger.debug('Applying filters');
		const filter = tempPublisher?.getVideoFilter();
		const videoSource = publisher?.getVideoSource();

		if (hasClearedBackground && updateRecording) {
			updateRecording({
				isBackgroundOff: true,
			});
		}
		try {
			if (filter) {
				setHasFilterApplied(true);
				await publisher?.applyVideoFilter(filter).then(
					() => {
						toast(t('settings_modal.settings_applied'), {
							hideProgressBar: true,
							type: 'success',
							autoClose: 10000,
						});
						const videoSource = publisher?.getVideoSource();
						localStorage.setItem('cameraId', videoSource?.deviceId ?? '');
						localStorage.setItem('cameraFilter', JSON.stringify(filter));
						customBugsnagLogger.debug('Filter applied');
						if (hasClearedBackground && updateRecording) {
							updateRecording({
								isBackgroundOff: true,
							});
						}
					},
					(reason) => {
						toast(
							`There has been an error applying the settings. Reason: ${reason}`,
							{
								hideProgressBar: true,
								type: 'error',
								autoClose: 10000,
							}
						);
					}
				);
			} else {
				setHasFilterApplied(false);
				await publisher?.clearVideoFilter().then(
					() => {
						toast(t('settings_modal.settings_applied'), {
							hideProgressBar: true,
							type: 'success',
							autoClose: 10000,
						});
						const videoSource = publisher?.getVideoSource();
						localStorage.setItem('cameraId', videoSource?.deviceId ?? '');
						localStorage.setItem('cameraFilter', 'none');
						customBugsnagLogger.debug('Filter cleared');
					},
					(reason) => {
						toast(
							`There has been an error applying the settings. Reason: ${reason}`,
							{
								hideProgressBar: true,
								type: 'error',
								autoClose: 10000,
							}
						);
					}
				);
			}
		} catch (e) {
			console.log(e);
		}
		if (
			tempCamera?.deviceId &&
			tempCamera?.deviceId !== videoSource?.deviceId
		) {
			customBugsnagLogger.debug(`Setting video source ${tempCamera?.deviceId}`);
			setDevice('video', tempCamera?.deviceId);
		}
	}, [
		publisher,
		tempPublisher,
		hasClearedBackground,
		updateRecording,
		tempCamera?.deviceId,
		retry,
		t,
		setDevice,
	]);

	// Dev-only QA helper: expose publisher and retry on window so we can
	// simulate the broken-publisher scenario from DevTools without going offline.
	//   window.__pub.destroy()          // simulate silent publisher death
	//   window.__pub.publishVideo(false) // toggle outbound video
	//   window.__retryPub()             // run usePublisher's retry()
	useEffect(() => {
		if (process.env.NODE_ENV !== 'development') return;
		(window as any).__pub = publisher;
		(window as any).__retryPub = () => retry(true);
		return () => {
			delete (window as any).__pub;
			delete (window as any).__retryPub;
		};
	}, [publisher, retry]);

	const applyMic = useCallback(() => {
		if (tempMic?.deviceId) {
			customBugsnagLogger.debug(`Setting mic source ${tempMic?.deviceId}`);
			publisher?.setAudioSource(tempMic?.deviceId);
			localStorage.setItem('micId', tempMic?.deviceId);
			toast(t('settings_modal.settings_applied'), {
				hideProgressBar: true,
				type: 'success',
				autoClose: 10000,
			});
		}
	}, [tempMic, publisher, t]);

	const values = useMemo(
		() => ({
			publisher,
			isLoading,
			publisherStream,
			videoElement,
			deviceStatus,
			tempDeviceStatus,
			toggleDevice,
			lastInteracted,
			clearBackground,
			setBackground,
			blurBackground,
			createTempPublisher,
			destroyTempPublisher,
			applyFilters,
			tempPublisher,
			tempStream,
			tempVideoElement,
			loadingFilter,
			canApplyFilters,
			hasFilterApplied,
			applyMic,
			hasClearedBackground,
			isReadyToPublish,
			retry,
		}),
		[
			publisher,
			isLoading,
			publisherStream,
			videoElement,
			deviceStatus,
			tempDeviceStatus,
			toggleDevice,
			lastInteracted,
			clearBackground,
			setBackground,
			blurBackground,
			createTempPublisher,
			destroyTempPublisher,
			applyFilters,
			tempPublisher,
			tempStream,
			tempVideoElement,
			loadingFilter,
			canApplyFilters,
			hasFilterApplied,
			applyMic,
			hasClearedBackground,
			isReadyToPublish,
			retry,
		]
	);

	return (
		<Context.Provider value={values}>
			{children}

			{showDeviceInUse && (
				<DeviceUsedByOtherProcess setShow={setShowDeviceInUse} />
			)}
			{deviceError.show && (
				<DeviceError
					setShow={setDeviceError}
					name={deviceError.name}
					type={deviceError.type}
				/>
			)}
		</Context.Provider>
	);
};

export const usePublisherContext = (): ContextType => useContext(Context);

export default PublisherProvider;
